import { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import ExpandableSection from "@/components/job/ExpandableSection";
import { Button } from "@/components/ui/button";
import { useConfigOptions } from "@/hooks/useConfigOptions";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";
import { filterMaterialGroupsByDesignCode } from "@/utils/materialFilters";

import FlangeClampComponent from "./components/FlangeClampComponent";
import StandardComponent from "./components/StandardComponent";
import TeeComponent from "./components/TeeComponent";
import type { SingleComponentData } from "./types";

type FieldErrors = {
  component_nps?: string;
  component_schedule?: string;
  flange_rating?: string;
  component_diameter?: string;
  unit_for_component_diameter?: string;
  raised_face_od?: string;
  flange_num_bolt_holes?: string;
  flange_stud_size?: string;
  flange_bolt_circle?: string;
};

const emptyInitialData: SingleComponentData = {
  component_nps: null,
  component_diameter: null,
  unit_for_component_diameter: "Inches",
  component_thickness: null,
  unit_for_component_thickness: "Inches",
  component_schedule: null,
};

const cardName = "componentDetails";
export function ComponentDetails() {
  const { sections, toggleExpandableSection, setComponentDetails } = useCUMechanicalJobStore(
    useShallow((state) => ({
      sections: state.sections,
      toggleExpandableSection: state.toggleExpandableSection,
      setComponentDetails: state.setComponentDetails,
    }))
  );

  const savedComponentDetails = sections.componentDetails;
  const repairType = sections.repairType.data.repairType;
  const designCode = sections.jobDetails.data.design_code;
  const [component_material, setComponent_material] = useState<null | number>(null);
  const [components_data, set_components_data] = useState<SingleComponentData[]>([emptyInitialData]);
  const [materialError, setMaterialError] = useState<string | null>(null);
  const [componentErrors, setComponentErrors] = useState<FieldErrors[]>([]);

  const { componentMaterialGroups } = useConfigOptions();
  const filteredMaterialGroups = useMemo(() => {
    const filtered = filterMaterialGroupsByDesignCode(componentMaterialGroups, designCode) ?? [];
    if (!component_material) {
      return filtered;
    }
    const hasSelectedMaterial = filtered.some((group) => {
      return group.options.some((option) => option.id === component_material);
    });
    return hasSelectedMaterial ? filtered : componentMaterialGroups;
  }, [componentMaterialGroups, designCode, component_material]);

  useEffect(() => {
    if (savedComponentDetails.isDataFilled) {
      setComponent_material(savedComponentDetails.data.component_material);
      set_components_data(savedComponentDetails.data.components_data);
    }
  }, [savedComponentDetails.isDataFilled]);

  function validateMaterial(): boolean {
    if (!component_material) {
      setMaterialError("Component material is required");
      return false;
    }
    setMaterialError(null);
    return true;
  }

  function validateSingleComponent(component: SingleComponentData): FieldErrors {
    const errors: FieldErrors = {};

    if (!component.component_nps) {
      errors.component_nps = "NPS is required";
      return errors;
    }

    if (component.component_nps === "custom") {
      if (!component.component_diameter) {
        errors.component_diameter = "Custom diameter is required";
      }
      if (!component.unit_for_component_diameter) {
        errors.unit_for_component_diameter = "Unit is required";
      }
      if (repairType === "flange_clamp") {
        if (!component.flange_num_bolt_holes) {
          errors.flange_num_bolt_holes = "Number of bolt holes is required";
        }
        if (!component.flange_stud_size) {
          errors.flange_stud_size = "Stud size is required";
        }
        if (!component.flange_bolt_circle) {
          errors.flange_bolt_circle = "Bolt circle is required";
        }
      }
    } else {
      if (repairType === "flange_clamp") {
        if (!component.flange_rating) {
          errors.flange_rating = "Flange rating is required";
        }
      } else {
        if (!component.component_schedule) {
          errors.component_schedule = "Schedule is required";
        }
      }
    }

    return errors;
  }

  function validateTeeComponentSizes(componentErrors: FieldErrors[]): FieldErrors[] {
    if (repairType !== "tee" || components_data.length < 2) {
      return componentErrors;
    }

    const nps1 = components_data[0]?.component_nps;
    const nps2 = components_data[1]?.component_nps;

    const bothAreStandardNPS = nps1 && nps2 && nps1 !== "custom" && nps2 !== "custom";
    if (!bothAreStandardNPS) {
      return componentErrors;
    }

    const npsValue1 = Number(nps1);
    const npsValue2 = Number(nps2);
    const isComponent2Larger = !isNaN(npsValue1) && !isNaN(npsValue2) && npsValue2 > npsValue1;

    if (isComponent2Larger) {
      componentErrors[1] = {
        ...componentErrors[1],
        component_nps: "Component 2 NPS must be less than or equal to Component 1 NPS.",
      };
    }

    return componentErrors;
  }

  function cleanComponentData(data: SingleComponentData): SingleComponentData {
    // For non-custom NPS, remove custom diameter/thickness fields by omitting them from the returned object
    if (data.component_nps !== "custom") {
      // Destructure to exclude the unwanted fields
      const {
        component_diameter,
        unit_for_component_diameter,
        component_thickness,
        unit_for_component_thickness,
        ...rest
      } = data;
      // Return the rest without the custom fields - they will be omitted from the API payload
      return rest as SingleComponentData;
    }
    // For custom NPS, keep all fields
    return data;
  }

  function handleSubmitForm() {
    const isMaterialValid = validateMaterial();

    const newComponentErrors = components_data.map((component) => validateSingleComponent(component));
    const componentErrorsWithTeeValidation = validateTeeComponentSizes(newComponentErrors);

    setComponentErrors(componentErrorsWithTeeValidation);

    const hasAnyErrors =
      !isMaterialValid || componentErrorsWithTeeValidation.some((errors) => Object.keys(errors).length > 0);

    if (!hasAnyErrors) {
      const cleanedData = components_data.map(cleanComponentData);
      setComponentDetails({
        status: "done",
        data: {
          component_options: [],
          components_data: cleanedData,
          component_material,
        },
      });
    }
  }

  const renderComponentByRepairType = () => {
    switch (repairType) {
      case "tee":
        return (
          <TeeComponent
            components_data={components_data}
            set_components_data={set_components_data}
            component_material={component_material}
            setComponent_material={setComponent_material}
            materialGroups={filteredMaterialGroups}
            materialError={materialError}
            componentErrors={componentErrors}
          />
        );
      case "flange_clamp":
        return (
          <FlangeClampComponent
            components_data={components_data}
            set_components_data={set_components_data}
            component_material={component_material}
            setComponent_material={setComponent_material}
            materialGroups={filteredMaterialGroups}
            materialError={materialError}
            componentError={componentErrors[0]}
          />
        );
      case "straight_line":
      case "long_radius":
      default:
        return (
          <StandardComponent
            components_data={components_data}
            set_components_data={set_components_data}
            component_material={component_material}
            setComponent_material={setComponent_material}
            materialGroups={filteredMaterialGroups}
            materialError={materialError}
            componentError={componentErrors[0]}
          />
        );
    }
  };

  return (
    <ExpandableSection
      status={savedComponentDetails.status}
      setIsExpanded={() => toggleExpandableSection(cardName)}
      title="Component Details"
    >
      <div className="flex flex-col">
        {renderComponentByRepairType()}
        <Button onClick={handleSubmitForm} className="mt-4 self-end">
          Save
        </Button>
      </div>
    </ExpandableSection>
  );
}

export default ComponentDetails;
